import { useState, useMemo, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, AssignChannelNumbersResponse } from '@/lib/api'
import { Download, Search, Check, X, AlertCircle, Loader2, CheckSquare, Square, Filter, Link2, Eye, EyeOff, Hash, Upload } from 'lucide-react'

interface Category {
  id: string
  name: string
  type: string
}

export default function Import() {
  const [importSource, setImportSource] = useState<'xui' | 'm3u'>('xui')

  // Panel credentials state
  const [panelUrl, setPanelUrl] = useState('')
  const [panelUsername, setPanelUsername] = useState('')
  const [panelPassword, setPanelPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isConnected, setIsConnected] = useState(false)

  // Filter/selection state
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [targetCategoryId, setTargetCategoryId] = useState<string>('')
  const [createCategories, setCreateCategories] = useState(false)
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number; errors?: string[] } | null>(null)
  const [assignResult, setAssignResult] = useState<AssignChannelNumbersResponse | null>(null)
  const [m3uFile, setM3uFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Category configuration dialog state
  const [showCategoryConfig, setShowCategoryConfig] = useState(false)
  const [newCategoriesToConfigure, setNewCategoriesToConfigure] = useState<Array<{ id: string; name: string }>>([])
  const [categoryConfigs, setCategoryConfigs] = useState<Record<string, { rangeStart: number; rangeEnd: number; autoAssign: boolean }>>({})

  const queryClient = useQueryClient()

  // Panel credentials for API calls
  const panelCreds = {
    panel_url: panelUrl,
    panel_username: panelUsername,
    panel_password: panelPassword,
  }

  // Fetch XUI streams - only when connected
  const { data: xuiData, isLoading: streamsLoading, error: streamsError, refetch: refetchStreams } = useQuery({
    queryKey: ['xui-streams', categoryFilter, search, panelUrl, panelUsername],
    queryFn: () => api.getXUIStreams({
      category_id: categoryFilter || undefined,
      search: search || undefined,
      ...panelCreds
    }),
    enabled: importSource === 'xui' && isConnected && !!panelUrl && !!panelUsername && !!panelPassword,
    staleTime: 30000,
    retry: false,
  })

  const m3uPreviewMutation = useMutation({
    mutationFn: (file: File) => api.previewM3UStreams(file),
    onSuccess: () => {
      setIsConnected(true)
      setImportResult(null)
      setSelectedIds(new Set())
      setCategoryFilter('')
    },
  })

  // Fetch OBA categories for target selection
  const { data: obaCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.getCategories({ type: 'live' }),
  })

  const activeData = importSource === 'm3u' ? m3uPreviewMutation.data : xuiData
  const streams = activeData?.streams || []
  const xuiCategories = activeData?.categories || []

  // Filter streams based on search (client-side additional filtering)
  const filteredStreams = useMemo(() => {
    if (!search) return streams
    const searchLower = search.toLowerCase()
    return streams.filter(s =>
      s.name.toLowerCase().includes(searchLower) ||
      s.category_name?.toLowerCase().includes(searchLower)
    )
  }, [streams, search])

  // Selectable streams (exclude already existing)
  const selectableStreams = filteredStreams.filter(s => !s.exists)
  const existingCount = filteredStreams.filter(s => s.exists).length

  // Import mutation
  const importMutation = useMutation({
    mutationFn: ({ streamIds, configs }: { streamIds: string[]; configs?: Array<{ panel_category_id: string; channel_range_start: number; channel_range_end: number; auto_assign_numbers: boolean }> }) => {
      if (importSource === 'm3u') {
        if (!m3uFile) throw new Error('M3U file is required')
        return api.importM3UStreams({
          file: m3uFile,
          stream_ids: streamIds,
          category_id: targetCategoryId || undefined,
          create_category: createCategories,
        })
      }
      return api.importXUIStreams({
        stream_ids: streamIds,
        category_id: targetCategoryId || undefined,
        create_category: createCategories,
        category_configs: configs,
        ...panelCreds
      })
    },
    onSuccess: (result) => {
      setImportResult(result)
      setSelectedIds(new Set())
      setShowCategoryConfig(false)
      setCategoryConfigs({})
      queryClient.invalidateQueries({ queryKey: ['streams'] })
      queryClient.invalidateQueries({ queryKey: ['xui-streams'] })
      queryClient.invalidateQueries({ queryKey: ['categories'] })
    },
  })

  // Assign channel numbers mutation
  const assignNumbersMutation = useMutation({
    mutationFn: () => api.assignChannelNumbers(),
    onSuccess: (result) => {
      setAssignResult(result)
      queryClient.invalidateQueries({ queryKey: ['streams'] })
      queryClient.invalidateQueries({ queryKey: ['categories'] })
    },
  })

  const handleConnect = () => {
    if (!panelUrl || !panelUsername || !panelPassword) {
      return
    }
    setIsConnected(true)
    setImportResult(null)
    // Reset filters when connecting to new panel
    setCategoryFilter('')
    setSelectedIds(new Set())
  }

  const handleM3UFileSelected = (file: File | null) => {
    setM3uFile(file)
    setSelectedIds(new Set())
    setImportResult(null)
    setIsConnected(false)
    if (file) {
      m3uPreviewMutation.mutate(file)
    }
  }

  const handleDisconnect = () => {
    setIsConnected(false)
    setSelectedIds(new Set())
    setCategoryFilter('')
    if (importSource === 'm3u') {
      setM3uFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      m3uPreviewMutation.reset()
    }
  }

  const handleSelectAll = () => {
    if (selectedIds.size === selectableStreams.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(selectableStreams.map(s => s.id)))
    }
  }

  const handleToggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
  }

  const handleImport = () => {
    if (selectedIds.size === 0) return
    setImportResult(null)

    // If creating categories, check for new ones that need configuration
    if (createCategories) {
      // Get unique category IDs from selected streams
      const selectedStreams = streams.filter(s => selectedIds.has(s.id))
      const selectedCategoryIds = new Set(selectedStreams.map(s => s.category_id))

      // Find categories that don't exist in OBA yet
      const existingCategoryNames = new Set((obaCategories || []).map((c: Category) => c.name.toLowerCase()))
      const newCategories = xuiCategories.filter(
        cat => selectedCategoryIds.has(cat.id) && !existingCategoryNames.has(cat.name.toLowerCase())
      )

      if (newCategories.length > 0) {
        // Initialize configs for new categories
        const initialConfigs: Record<string, { rangeStart: number; rangeEnd: number; autoAssign: boolean }> = {}
        newCategories.forEach((cat, index) => {
          const baseNumber = 1000 + (index * 1000)
          initialConfigs[cat.id] = {
            rangeStart: baseNumber,
            rangeEnd: baseNumber + 999,
            autoAssign: true
          }
        })
        setCategoryConfigs(initialConfigs)
        setNewCategoriesToConfigure(newCategories)
        setShowCategoryConfig(true)
        return
      }
    }

    // No new categories or not creating categories - proceed directly
    importMutation.mutate({ streamIds: Array.from(selectedIds) })
  }

  const handleConfirmImport = () => {
    // Convert configs to API format
    const configs = Object.entries(categoryConfigs).map(([panelCategoryId, cfg]) => ({
      panel_category_id: panelCategoryId,
      channel_range_start: cfg.rangeStart,
      channel_range_end: cfg.rangeEnd,
      auto_assign_numbers: cfg.autoAssign
    }))

    importMutation.mutate({ streamIds: Array.from(selectedIds), configs })
  }

  const isAllSelected = selectableStreams.length > 0 && selectedIds.size === selectableStreams.length
  const canConnect = panelUrl && panelUsername && panelPassword
  const isLoadingStreams = importSource === 'm3u' ? m3uPreviewMutation.isPending : streamsLoading
  const streamLoadError = importSource === 'm3u' ? m3uPreviewMutation.error : streamsError

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Import Channels</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base">Import live channels from your XUI/XtreamCodes panel or an M3U playlist into OBA</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <button
            onClick={() => assignNumbersMutation.mutate()}
            disabled={assignNumbersMutation.isPending}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            title="Assign channel numbers to streams that don't have them"
          >
            {assignNumbersMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Hash className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">Assign Channel Numbers</span>
            <span className="sm:hidden">Assign #</span>
          </button>
          {isConnected && (
            <button
              onClick={handleImport}
              disabled={selectedIds.size === 0 || importMutation.isPending}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              {importMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Import ({selectedIds.size})
            </button>
          )}
        </div>
      </div>

      {/* Import Source */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { setImportSource('xui'); setIsConnected(false); setSelectedIds(new Set()); setImportResult(null) }}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${importSource === 'xui' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
          >
            XUI / XtreamCodes Panel
          </button>
          <button
            onClick={() => { setImportSource('m3u'); setIsConnected(false); setSelectedIds(new Set()); setImportResult(null) }}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${importSource === 'm3u' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
          >
            Upload M3U File
          </button>
        </div>
      </div>

      {importSource === 'xui' ? (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Link2 className="w-5 h-5" />
          Panel Connection
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Panel URL</label>
            <input
              type="text"
              value={panelUrl}
              onChange={(e) => setPanelUrl(e.target.value)}
              placeholder="http://your-panel.com:8080"
              disabled={isConnected}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Username</label>
            <input
              type="text"
              value={panelUsername}
              onChange={(e) => setPanelUsername(e.target.value)}
              placeholder="username"
              disabled={isConnected}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={panelPassword}
                onChange={(e) => setPanelPassword(e.target.value)}
                placeholder="password"
                disabled={isConnected}
                className="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          {!isConnected ? (
            <button
              onClick={handleConnect}
              disabled={!canConnect}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Link2 className="w-4 h-4" />
              Connect & Load Channels
            </button>
          ) : (
            <button
              onClick={handleDisconnect}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              <X className="w-4 h-4" />
              Disconnect
            </button>
          )}
          {isConnected && (
            <span className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <Check className="w-4 h-4" />
              Connected
            </span>
          )}
        </div>
      </div>
      ) : (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5" />
          M3U File Upload
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Upload a .m3u or .m3u8 playlist. OBA will parse channels, logos, EPG IDs, and group-title categories.</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".m3u,.m3u8,audio/x-mpegurl,application/vnd.apple.mpegurl,text/plain"
          onChange={(e) => handleM3UFileSelected(e.target.files?.[0] || null)}
          className="block w-full text-sm text-gray-700 dark:text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700"
        />
        {m3uPreviewMutation.isPending && (
          <p className="mt-3 flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400"><Loader2 className="w-4 h-4 animate-spin" />Parsing playlist...</p>
        )}
        {m3uPreviewMutation.error && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400">Failed to parse M3U file. Make sure it is a valid playlist.</p>
        )}
        {isConnected && m3uFile && (
          <div className="mt-4 flex items-center gap-3">
            <span className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <Check className="w-4 h-4" />
              Loaded {streams.length} channels from {m3uFile.name}
            </span>
            <button onClick={handleDisconnect} className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Clear</button>
          </div>
        )}
      </div>
      )}

      {/* Import Result */}
      {importResult && (
        <div className={`p-4 rounded-lg ${importResult.errors?.length ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800' : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'}`}>
          <div className="flex items-start gap-3">
            {importResult.errors?.length ? (
              <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            ) : (
              <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            )}
            <div>
              <p className="font-medium text-gray-900 dark:text-white">
                Import Complete: {importResult.imported} imported, {importResult.skipped} skipped (duplicates)
              </p>
              {importResult.errors && importResult.errors.length > 0 && (
                <ul className="mt-2 text-sm text-yellow-700 dark:text-yellow-300 list-disc list-inside">
                  {importResult.errors.slice(0, 5).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                  {importResult.errors.length > 5 && (
                    <li>...and {importResult.errors.length - 5} more errors</li>
                  )}
                </ul>
              )}
            </div>
            <button onClick={() => setImportResult(null)} className="ml-auto text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Channel Number Assignment Result */}
      {assignResult && (
        <div className={`p-4 rounded-lg ${assignResult.assigned > 0 ? 'bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800' : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'}`}>
          <div className="flex items-start gap-3">
            <Hash className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-gray-900 dark:text-white">
                {assignResult.assigned > 0
                  ? `Channel Numbers Assigned: ${assignResult.assigned} streams updated`
                  : 'No streams needed channel numbers assigned'}
              </p>
              {assignResult.skipped > 0 && (
                <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                  {assignResult.skipped} streams skipped (no available numbers in range)
                </p>
              )}
              {assignResult.categories && assignResult.categories.length > 0 && (
                <ul className="mt-2 text-sm text-gray-600 dark:text-gray-400 list-disc list-inside">
                  {assignResult.categories.map((cat, i) => (
                    <li key={i}>{cat.category}: {cat.assigned} assigned{cat.skipped > 0 ? `, ${cat.skipped} skipped` : ''}</li>
                  ))}
                </ul>
              )}
            </div>
            <button onClick={() => setAssignResult(null)} className="ml-auto text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Show channel browser only when connected */}
      {isConnected && (
        <>
          {/* Filters */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex flex-wrap gap-4">
              {/* Search */}
              <div className="flex-1 min-w-0 sm:min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search Channels</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* XUI Category Filter */}
              <div className="w-full sm:w-64">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{importSource === 'm3u' ? 'M3U Category' : 'XUI Category'}</label>
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">All Categories</option>
                    {xuiCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Category Assignment Options */}
              <div className="w-full sm:w-80">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category Assignment</label>
                <div className="space-y-2">
                  {/* Option 1: Select specific OBA category */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="categoryMode"
                      checked={!createCategories}
                      onChange={() => setCreateCategories(false)}
                      className="w-4 h-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Import to specific category:</span>
                  </label>
                  <select
                    value={targetCategoryId}
                    onChange={(e) => {
                      setTargetCategoryId(e.target.value)
                      setCreateCategories(false)
                    }}
                    disabled={createCategories}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:text-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">No category (root level)</option>
                    {(obaCategories || []).map((cat: Category) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>

                  {/* Option 2: Auto-create from XUI */}
                  <label className="flex items-center gap-2 cursor-pointer mt-2">
                    <input
                      type="radio"
                      name="categoryMode"
                      checked={createCategories}
                      onChange={() => {
                        setCreateCategories(true)
                        setTargetCategoryId('')
                      }}
                      className="w-4 h-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Auto-create categories from source</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Selection Summary */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-gray-50 dark:bg-gray-800 rounded-lg px-4 py-3">
            <div className="flex items-center gap-4">
              <button
                onClick={handleSelectAll}
                className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                {isAllSelected ? (
                  <>
                    <CheckSquare className="w-4 h-4" />
                    Deselect All
                  </>
                ) : (
                  <>
                    <Square className="w-4 h-4" />
                    Select All ({selectableStreams.length})
                  </>
                )}
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {selectedIds.size} selected
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                Total: <span className="font-medium">{filteredStreams.length}</span> channels
              </span>
              {existingCount > 0 && (
                <span className="text-yellow-600 dark:text-yellow-400">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  {existingCount} already in OBA
                </span>
              )}
            </div>
          </div>

          {/* Streams Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            {isLoadingStreams ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <span className="ml-3 text-gray-600 dark:text-gray-400">{importSource === 'm3u' ? 'Parsing channels from M3U file...' : 'Loading channels from XUI panel...'}</span>
              </div>
            ) : streamLoadError ? (
              <div className="flex flex-col items-center justify-center py-12 text-red-600 dark:text-red-400">
                <AlertCircle className="w-8 h-8 mb-2" />
                <p className="font-medium">Failed to load channels</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {importSource === 'm3u' ? 'Check your playlist file and try again' : 'Check your panel credentials and try again'}
                </p>
                <button
                  onClick={() => importSource === 'm3u' && m3uFile ? m3uPreviewMutation.mutate(m3uFile) : refetchStreams()}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : filteredStreams.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                <Search className="w-8 h-8 mb-2" />
                <p>No channels found</p>
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="text-sm text-blue-600 hover:underline mt-2"
                  >
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="w-12 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Channel
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {importSource === 'm3u' ? 'M3U Category' : 'XUI Category'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredStreams.map((stream) => (
                      <tr
                        key={stream.id}
                        className={`${stream.exists ? 'bg-gray-50 dark:bg-gray-900 opacity-60' : 'hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer'}`}
                        onClick={() => !stream.exists && handleToggleSelect(stream.id)}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(stream.id)}
                            onChange={() => handleToggleSelect(stream.id)}
                            disabled={stream.exists}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {stream.icon ? (
                              <img
                                src={stream.icon}
                                alt=""
                                className="w-8 h-8 rounded object-cover bg-gray-100"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none'
                                }}
                              />
                            ) : (
                              <div className="w-8 h-8 rounded bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-400 text-xs">
                                TV
                              </div>
                            )}
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">{stream.name}</div>
                              {stream.epg_channel_id && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">EPG: {stream.epg_channel_id}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {stream.category_name || '-'}
                        </td>
                        <td className="px-4 py-3">
                          {stream.exists ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                              <AlertCircle className="w-3 h-3" />
                              Already exists
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                              <Check className="w-3 h-3" />
                              Available
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Instructions when not connected */}
      {!isConnected && importSource === 'xui' && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 text-center">
          <Link2 className="w-12 h-12 mx-auto mb-4 text-blue-600 dark:text-blue-400" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Connect to your XUI Panel</h3>
          <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
            Enter your XUI/XtreamCodes panel URL and credentials above, then click "Connect & Load Channels" to browse and import channels.
          </p>
        </div>
      )}

      {/* Category Configuration Modal */}
      {showCategoryConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Configure New Categories</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Set channel number ranges for the new categories being created
              </p>
            </div>

            <div className="p-6 overflow-y-auto max-h-[50vh] space-y-6">
              {newCategoriesToConfigure.map((cat) => (
                <div key={cat.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 dark:text-white mb-4">{cat.name}</h3>

                  <div className="flex items-center gap-2 mb-4">
                    <input
                      type="checkbox"
                      id={`auto-${cat.id}`}
                      checked={categoryConfigs[cat.id]?.autoAssign ?? true}
                      onChange={(e) => setCategoryConfigs(prev => ({
                        ...prev,
                        [cat.id]: { ...prev[cat.id], autoAssign: e.target.checked }
                      }))}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor={`auto-${cat.id}`} className="text-sm text-gray-700 dark:text-gray-300">
                      Auto-assign channel numbers
                    </label>
                  </div>

                  {categoryConfigs[cat.id]?.autoAssign && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Start Number
                        </label>
                        <input
                          type="number"
                          value={categoryConfigs[cat.id]?.rangeStart || 1000}
                          onChange={(e) => setCategoryConfigs(prev => ({
                            ...prev,
                            [cat.id]: { ...prev[cat.id], rangeStart: parseInt(e.target.value) || 0 }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          End Number
                        </label>
                        <input
                          type="number"
                          value={categoryConfigs[cat.id]?.rangeEnd || 1999}
                          onChange={(e) => setCategoryConfigs(prev => ({
                            ...prev,
                            [cat.id]: { ...prev[cat.id], rangeEnd: parseInt(e.target.value) || 0 }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCategoryConfig(false)
                  setCategoryConfigs({})
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmImport}
                disabled={importMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {importMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Import {selectedIds.size} Channels
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
